import { useState, useEffect } from 'react';
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import { Shield, Fingerprint, CheckCircle2, AlertTriangle, X, Loader, ScanFace, Pencil } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import {
  requestPasskeyRegistrationOptions,
  verifyPasskeyRegistration,
} from '../../security/passkey/passkeyApi';

interface Props {
  onClose: () => void;
  onSuccess?: (trustLevel: number) => void;
  onSkip?: () => void;
}

type Etapa = 'nomear' | 'biometria' | 'sucesso' | 'ja_cadastrado' | 'erro';

/**
 * PasskeySetupModal — Wizard premium de cadastro de Passkey (WebAuthn).
 *
 * Fluxo:
 *   1. Verifica se o browser suporta WebAuthn e autenticador de plataforma
 *   2. Usuário nomeia a chave/estação (ex: "Robson — iPhone")
 *   3. Browser abre biometria nativa (Face ID / Touch ID / Windows Hello)
 *   4. Envia resposta ao backend validar
 *   5. Confirma cadastro e eleva trustLevel para T5
 *
 * Ref: 04-passkey-registration.md
 */
export function PasskeySetupModal({ onClose, onSuccess, onSkip }: Props) {
  const [etapa, setEtapa] = useState<Etapa>('nomear');
  const [nomeDispositivo, setNomeDispositivo] = useState('');
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState<{ nome: string; trustLevel: number } | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const { user } = useAuthStore();

  const isMobile = typeof window !== 'undefined' && (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768);

  const getHora = () => new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  async function iniciarRegistro(customName?: string) {
    const nome = (customName || nomeDispositivo || (isMobile ? 'Celular' : 'Estação')).trim();
    setEtapa('biometria');
    setErro('');

    try {
      // 1. Buscar opções do servidor com flag isMobile
      console.log('[Passkey] 🚀 Iniciando registro — nome:', nome, '| isMobile:', isMobile);
      const serverData = await requestPasskeyRegistrationOptions({ nomeDispositivo: nome, isMobile });
      const { options } = serverData as any;
      console.log('[Passkey] 📋 Server options recebidas:', JSON.stringify(serverData, null, 2));
      console.log('[Passkey] 🔑 rp.id:', options.rp?.id);
      console.log('[Passkey] 👤 user.name:', options.user?.name);
      console.log('[Passkey] 🔐 authenticatorSelection:', JSON.stringify(options.authenticatorSelection));
      console.log('[Passkey] 💡 hints do servidor:', options.hints);

      // 2. Passkey local da máquina / celular via WebAuthn
      const base64urlToBuffer = (b64url: string): ArrayBuffer => {
        const padding = '='.repeat((4 - b64url.length % 4) % 4);
        const base64 = (b64url + padding).replace(/-/g, '+').replace(/_/g, '/');
        const raw = atob(base64);
        const buffer = new ArrayBuffer(raw.length);
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
        return buffer;
      };
      const bufferToBase64url = (buf: ArrayBuffer): string => {
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (const b of bytes) binary += String.fromCharCode(b);
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      };

      const publicKey: any = {
        challenge: base64urlToBuffer(options.challenge),
        rp: {
          name: options.rp.name,
          id: options.rp.id,
        },
        user: {
          id: base64urlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },    // ES256
          { type: 'public-key', alg: -257 },  // RS256
          { type: 'public-key', alg: -8 },    // EdDSA
        ],
        timeout: 180000,
        attestation: 'none',
        excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
          id: base64urlToBuffer(c.id),
          type: 'public-key' as const,
          transports: ['internal'] as const,
        })),
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'required',
          requireResidentKey: true,
          userVerification: 'required',
        },
        extensions: {
          credProps: true,
        },
        hints: ['client-device'],
      };

      console.log('[Passkey] ⏳ Chamando navigator.credentials.create()...');
      const t0 = performance.now();

      const rawCred = await navigator.credentials.create({
        publicKey,
      }) as PublicKeyCredential;

      const elapsed = Math.round(performance.now() - t0);
      console.log('[Passkey] ✅ credentials.create() retornou em', elapsed, 'ms');

      if (!rawCred) throw new Error('Nenhuma credencial criada');

      const att = rawCred.response as AuthenticatorAttestationResponse;
      const credential = {
        id: rawCred.id,
        rawId: bufferToBase64url(rawCred.rawId),
        type: rawCred.type,
        response: {
          clientDataJSON: bufferToBase64url(att.clientDataJSON),
          attestationObject: bufferToBase64url(att.attestationObject),
        },
        clientExtensionResults: rawCred.getClientExtensionResults(),
        authenticatorAttachment: (rawCred as any).authenticatorAttachment,
      };

      // 3. Enviar resposta ao backend para validar
      console.log('[Passkey] 📡 Enviando para register-verify...');
      const data = await verifyPasskeyRegistration(credential, { nomeDispositivo: nome }) as any;
      console.log('[Passkey] 🎉 SUCESSO! Dados:', data);

      setResultado({ nome: data.nome, trustLevel: data.trustLevel });
      setEtapa('sucesso');
      onSuccess?.(data.trustLevel);

    } catch (e: any) {
      const errName = e?.name || '';
      const msg = e?.message ?? String(e);
      console.error('[Passkey] 💥 ERRO:', errName, msg);

      const isInvalidState =
        errName === 'InvalidStateError' ||
        msg.toLowerCase().includes('invalid state') ||
        msg.toLowerCase().includes('already registered') ||
        msg.toLowerCase().includes('the object is in an invalid state');

      const isCancelled =
        errName === 'NotAllowedError' ||
        msg.toLowerCase().includes('notallowederror') ||
        msg.toLowerCase().includes('cancelled') ||
        msg.toLowerCase().includes('canceled') ||
        msg.toLowerCase().includes('user cancelled') ||
        msg.toLowerCase().includes('abort');

      if (isInvalidState) {
        console.log('[Passkey] ℹ️ Credencial existente reconhecida no autenticador.');
        setResultado({ nome: nomeDispositivo || (isMobile ? 'iPhone / Celular' : 'Computador'), trustLevel: 5 });
        setEtapa('ja_cadastrado');
        onSuccess?.(5);
      } else if (isCancelled) {
        setErro('Você cancelou a autorização biométrica no aparelho. Tente novamente quando quiser.');
        setEtapa('erro');
      } else {
        setErro(msg);
        setEtapa('erro');
      }
    }
  }

  // Detectar suporte WebAuthn e autenticador de plataforma
  useEffect(() => {
    let mounted = true;

    async function checkWebAuthnSupport() {
      if (typeof window === 'undefined' || typeof window.PublicKeyCredential === 'undefined') {
        if (mounted) {
          setErro('Seu navegador não suporta Chaves de Acesso (Passkeys). Recomendamos atualizar seu navegador ou sistema operacional.');
          setEtapa('erro');
        }
        return;
      }

      try {
        if (window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
          const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          if (available === false) {
            console.warn('[Passkey] Autenticador de plataforma não disponível.');
          }
        }
      } catch (checkErr) {
        console.warn('[Passkey] Erro na verificação de plataforma:', checkErr);
      }

      if (!mounted) return;

      // Sugestão de nome inteligente baseada no User Agent + Nome do Usuário
      const ua = navigator.userAgent;
      const code = Math.random().toString(16).slice(2, 6).toUpperCase(); // ex: A3F2
      const userName = user?.nome ? user.nome.trim().split(' ')[0] : '';
      const prefix = userName ? `${userName} — ` : '';

      let devType = 'Dispositivo';
      if (isMobile) {
        if (/iPhone|iPad|iPod/i.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document)) {
          devType = 'iPhone';
        } else {
          devType = 'Celular Android';
        }
      } else {
        if (ua.includes('Windows')) devType = 'Windows';
        else if (ua.includes('Macintosh') || ua.includes('Mac OS')) devType = 'macOS';
        else if (ua.includes('Linux') && !ua.includes('Android')) devType = 'Linux';
        else devType = 'Computador';
      }

      const name = `${prefix}${devType} — ${getHora()} #${code}`;

      setNomeDispositivo(name);
      setEtapa('nomear');
    }

    checkWebAuthnSupport();
    return () => { mounted = false; };
  }, [user]);

  const cardStyle = {
    ...card,
    ...(isMobile ? {
      background: 'linear-gradient(135deg, #e6fced 0%, #f0fff4 100%)',
      border: '2px solid rgba(0, 255, 135, 0.45)',
    } : {})
  };

  const closeBtnStyle = {
    ...closeBtn,
    ...(isMobile ? {
      background: 'rgba(0, 255, 135, 0.15)',
      color: '#065f46',
    } : {})
  };

  const secondaryBtnStyle = {
    ...secondaryBtn,
    ...(isMobile ? {
      color: '#065f46',
      borderColor: 'rgba(0, 255, 135, 0.3)',
      background: 'rgba(0, 255, 135, 0.1)',
    } : {})
  };

  const skipBtnStyle = {
    ...skipBtn,
    ...(isMobile ? {
      color: 'rgba(6, 95, 70, 0.6)',
      borderColor: 'rgba(0, 255, 135, 0.2)',
      background: 'transparent',
    } : {})
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div 
        className={isMobile ? 'neon-glow-card-biometria' : ''} 
        style={cardStyle} 
        onClick={e => e.stopPropagation()}
      >

        {/* Botão X */}
        <button onClick={onClose} style={closeBtnStyle}>
          <X size={18} />
        </button>

        {/* ── ETAPA: NOMEAR ── */}
        {etapa === 'nomear' && (
          <>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: isMobile ? 12 : 20,
              textAlign: isMobile ? 'center' : 'left',
              marginBottom: 20,
              width: '100%'
            }}>
              {/* Coluna Esquerda: Ícone de digital */}
              <div style={{ 
                ...iconWrap, 
                background: isMobile ? '#e6fced' : '#f5f3ff', 
                borderColor: isMobile ? 'rgba(0, 255, 135, 0.5)' : '#ddd6fe', 
                flexShrink: 0, 
                margin: 0 
              }}>
                <Fingerprint 
                  size={32} 
                  strokeWidth={1.8} 
                  className={isMobile ? 'neon-pulse-icon-biometria' : ''} 
                  style={{ color: isMobile ? '#059669' : '#6c5ce7' }} 
                />
              </div>

              {/* Coluna Direita: Textos */}
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <h2 style={{ ...titleSt, margin: 0, lineHeight: 1.25, color: isMobile ? '#065f46' : '#1e293b' }}>
                  {isMobile ? 'Cadastrar Chave de Acesso' : 'Cadastrar Chave de Acesso'}
                </h2>
                <p style={{ ...subtitleSt, margin: '4px 0 0', lineHeight: 1.4, color: isMobile ? 'rgba(6, 95, 70, 0.8)' : '#64748b' }}>
                  {isMobile ? (
                    <>Crie uma <strong>chave de acesso segura (Passkey)</strong> para esta conta. O seu aparelho confirmará sua identidade usando Face ID, impressão digital ou código do dispositivo.</>
                  ) : (
                    <>Uma <strong>Chave de Acesso (Passkey)</strong> será criada neste computador. Com ela, você entrará diretamente sem precisar autorizar pelo celular nas próximas vezes.</>
                  )}
                </p>
              </div>
            </div>

            {/* Guia Informativo: Windows Hello / Hello Windows */}
            {!isMobile && (
              <div style={{ ...tipBox, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Shield size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ textAlign: 'left' }}>
                    <strong style={{ color: '#92400e', display: 'block', fontSize: '0.8rem', marginBottom: 2 }}>Recomendação importante:</strong>
                    Ao abrir a janela do sistema, escolha a opção de criar a chave no <strong>Windows Hello</strong> (neste computador). Isso deixará a chave ativa e disponível em todos os navegadores da máquina.
                  </div>
                </div>
              </div>
            )}

            <div style={{ position: 'relative', width: '100%', marginTop: 4, marginBottom: 16 }}>
              <Pencil size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                value={nomeDispositivo}
                onChange={e => setNomeDispositivo(e.target.value)}
                placeholder={isMobile ? 'Nome para este celular (Ex: Meu iPhone)' : 'Nome para este computador (Ex: Notebook Secretaria)'}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                style={{
                  ...inputSt,
                  ...(isMobile ? {
                    background: '#fcfdfd',
                    color: '#065f46',
                    borderColor: isInputFocused ? 'rgba(0, 255, 135, 0.85)' : 'rgba(0, 255, 135, 0.3)',
                    boxShadow: isInputFocused ? '0 0 10px rgba(0, 255, 135, 0.25)' : 'none',
                  } : {
                    borderColor: isInputFocused ? '#6c5ce7' : '#e2e8f0',
                  })
                }}
                autoFocus
                maxLength={60}
              />
            </div>

            <button
              onClick={() => iniciarRegistro()}
              disabled={!nomeDispositivo.trim()}
              style={{
                ...primaryBtn,
                background: nomeDispositivo.trim()
                  ? (isMobile ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #6c5ce7, #a29bfe)')
                  : '#e2e8f0',
                color: nomeDispositivo.trim() ? '#fff' : '#94a3b8',
                cursor: nomeDispositivo.trim() ? 'pointer' : 'not-allowed',
                boxShadow: nomeDispositivo.trim() 
                  ? (isMobile ? '0 6px 24px rgba(5, 150, 105, 0.3)' : '0 6px 24px rgba(108, 92, 231, 0.3)') 
                  : 'none',
              }}
            >
              <ScanFace size={18} /> {isMobile ? 'Cadastrar Chave de Acesso' : 'Criar Chave de Acesso'}
            </button>

            {onSkip && (
              <button type="button" onClick={onSkip} style={skipBtnStyle}>
                {isMobile ? 'Pular — não cadastrar chave agora' : 'Pular — não cadastrar chave de acesso'}
              </button>
            )}

            <p style={{ ...footerNote, color: isMobile ? 'rgba(6, 95, 70, 0.7)' : '#94a3b8', lineHeight: 1.4 }}>
              {isMobile ? (
                <>🔒 A autenticação é realizada pelo próprio dispositivo. O sistema não recebe nem armazena sua imagem facial ou dados biométricos.</>
              ) : (
                <>🔒 Chave de acesso local protegida pelo dispositivo. Seus dados biométricos nunca saem deste computador.</>
              )}
            </p>
          </>
        )}

        {/* ── ETAPA: BIOMETRIA ── */}
        {etapa === 'biometria' && (
          <>
            <div style={{ 
              ...iconWrap, 
              background: isMobile ? '#e6fced' : '#eff6ff', 
              borderColor: isMobile ? 'rgba(0, 255, 135, 0.5)' : '#bfdbfe' 
            }}>
              <Loader 
                size={32} 
                strokeWidth={1.8} 
                style={{ 
                  color: isMobile ? '#059669' : '#3b82f6', 
                  animation: 'pk-spin 1s linear infinite' 
                }} 
              />
            </div>

            <h2 style={{ ...titleSt, color: isMobile ? '#065f46' : '#1e293b' }}>
              {isMobile ? 'Aguardando confirmação no aparelho...' : 'Aguardando chave de acesso...'}
            </h2>
            <p style={{ ...subtitleSt, color: isMobile ? 'rgba(6, 95, 70, 0.8)' : '#64748b' }}>
              O sistema solicitou a autorização de segurança.<br />
              Confirme usando Face ID, Touch ID ou código do dispositivo.
            </p>
          </>
        )}

        {/* ── ETAPA: SUCESSO ── */}
        {etapa === 'sucesso' && resultado && (
          <>
            <div style={{ ...iconWrap, background: '#ecfdf5', borderColor: '#bbf7d0' }}>
              <CheckCircle2 size={32} strokeWidth={1.8} style={{ color: '#16a34a' }} />
            </div>

            <h2 style={{ ...titleSt, color: '#16a34a' }}>Acesso rápido ativo!</h2>
            <p style={{ ...subtitleSt, color: isMobile ? 'rgba(6, 95, 70, 0.8)' : '#64748b' }}>
              <strong style={{ color: isMobile ? '#065f46' : '#1e293b' }}>"{resultado.nome}"</strong> agora é um dispositivo seguro e vinculado.
            </p>

            <div style={{ 
              ...successBox,
              ...(isMobile ? {
                background: 'rgba(0, 255, 135, 0.1)',
                borderColor: 'rgba(0, 255, 135, 0.3)',
              } : {})
            }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <span style={{ ...badge, background: '#ecfdf5', color: '#16a34a' }}>✓ Biometria / Passkey ativa</span>
                <span style={{ 
                  ...badge, 
                  background: isMobile ? 'rgba(0, 255, 135, 0.15)' : '#f5f3ff', 
                  color: isMobile ? '#065f46' : '#6c5ce7' 
                }}>🛡️ Confiança T{resultado.trustLevel}</span>
              </div>
            </div>

            <button onClick={onClose} style={{
              ...primaryBtn,
              background: 'linear-gradient(135deg, #16a34a, #22c55e)',
              boxShadow: '0 6px 24px rgba(22, 163, 74, 0.3)',
            }}>
              ✓ Concluir
            </button>
          </>
        )}

        {/* ── ETAPA: JÁ CADASTRADO (InvalidState / Chave existente no Keychain) ── */}
        {etapa === 'ja_cadastrado' && resultado && (
          <>
            <div style={{ ...iconWrap, background: '#ecfdf5', borderColor: '#bbf7d0' }}>
              <CheckCircle2 size={32} strokeWidth={1.8} style={{ color: '#16a34a' }} />
            </div>

            <h2 style={{ ...titleSt, color: '#16a34a' }}>Chave Já Cadastrada!</h2>
            <p style={{ ...subtitleSt, color: isMobile ? 'rgba(6, 95, 70, 0.85)' : '#64748b' }}>
              Este dispositivo (ou suas Chaves do iCloud/Google) já possui uma <strong>Chave de Acesso segura</strong> ativa para esta conta.
            </p>

            <div style={{ 
              ...successBox,
              ...(isMobile ? {
                background: 'rgba(0, 255, 135, 0.1)',
                borderColor: 'rgba(0, 255, 135, 0.3)',
              } : {})
            }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <span style={{ ...badge, background: '#ecfdf5', color: '#16a34a' }}>✓ Acesso biométrico pronto</span>
                <span style={{ 
                  ...badge, 
                  background: isMobile ? 'rgba(0, 255, 135, 0.15)' : '#f5f3ff', 
                  color: isMobile ? '#065f46' : '#6c5ce7' 
                }}>🛡️ Confiança T{resultado.trustLevel}</span>
              </div>
            </div>

            <button onClick={onClose} style={{
              ...primaryBtn,
              background: 'linear-gradient(135deg, #16a34a, #22c55e)',
              boxShadow: '0 6px 24px rgba(22, 163, 74, 0.3)',
            }}>
              ✓ Concluir / Usar Acesso Rápido
            </button>
          </>
        )}

        {/* ── ETAPA: ERRO ── */}
        {etapa === 'erro' && (
          <>
            <div style={{ ...iconWrap, background: '#fef2f2', borderColor: '#fecaca' }}>
              <AlertTriangle size={32} strokeWidth={1.8} style={{ color: '#ef4444' }} />
            </div>

            <h2 style={{ ...titleSt, color: isMobile ? '#7f1d1d' : '#1e293b' }}>Não foi possível cadastrar</h2>
            <p style={{ ...subtitleSt, color: '#ef4444' }}>{erro}</p>

            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <button onClick={onClose} style={secondaryBtnStyle}>
                Cancelar
              </button>
              <button onClick={() => setEtapa('nomear')} style={{
                ...primaryBtn,
                flex: 2,
                background: isMobile 
                  ? 'linear-gradient(135deg, #059669, #10b981)' 
                  : 'linear-gradient(135deg, #6c5ce7, #a29bfe)',
                boxShadow: isMobile 
                  ? '0 6px 24px rgba(5, 150, 105, 0.3)' 
                  : '0 6px 24px rgba(108, 92, 231, 0.3)',
              }}>
                Tentar novamente
              </button>
            </div>
          </>
        )}

        <style>{`
          @keyframes pk-spin { to { transform: rotate(360deg); } }
          @keyframes neon-pulse-biometria {
            0% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(0, 255, 135, 0.4)); }
            50% { transform: scale(1.08); filter: drop-shadow(0 0 8px rgba(0, 255, 135, 0.75)); }
            100% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(0, 255, 135, 0.4)); }
          }
          .neon-pulse-icon-biometria {
            animation: neon-pulse-biometria 1.8s infinite ease-in-out;
          }
          @keyframes neon-glow-biometria {
            0%, 100% {
              box-shadow: 0 0 10px rgba(0, 255, 135, 0.25), inset 0 0 4px rgba(0, 255, 135, 0.05);
              border-color: rgba(0, 255, 135, 0.45);
            }
            50% {
              box-shadow: 0 0 22px rgba(0, 255, 135, 0.8), inset 0 0 8px rgba(0, 255, 135, 0.15);
              border-color: rgba(0, 255, 135, 0.95);
            }
          }
          .neon-glow-card-biometria {
            animation: neon-glow-biometria 2.2s infinite ease-in-out;
            border: 2px solid rgba(0, 255, 135, 0.45) !important;
          }
        `}</style>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(12px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 99999, padding: 20,
  animation: 'pk-fadeIn 0.3s ease',
};

const card: React.CSSProperties = {
  background: '#ffffff', borderRadius: 24, padding: 'clamp(24px, 5vw, 36px)',
  maxWidth: 440, width: '100%', position: 'relative',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
  boxShadow: '0 25px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)',
  animation: 'pk-slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
  textAlign: 'center',
};

const closeBtn: React.CSSProperties = {
  position: 'absolute', top: 14, right: 14,
  background: '#f1f5f9', border: 'none', borderRadius: 8,
  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#64748b', transition: 'all 0.2s',
};

const iconWrap: React.CSSProperties = {
  width: 64, height: 64, borderRadius: 20,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: '2px solid #ddd6fe', marginBottom: 4,
};

const titleSt: React.CSSProperties = {
  fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', margin: '4px 0 0',
};

const subtitleSt: React.CSSProperties = {
  fontSize: '0.82rem', color: '#64748b', marginBottom: 8, lineHeight: 1.6,
};

const inputSt: React.CSSProperties = {
  width: '100%', padding: '13px 16px 13px 40px', borderRadius: 14,
  border: '2px solid #e2e8f0', background: '#f8fafc',
  color: '#1e293b', fontSize: '0.9rem', outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.2s',
};

const primaryBtn: React.CSSProperties = {
  width: '100%', padding: '13px', borderRadius: 14, border: 'none',
  color: '#fff', fontWeight: 700, fontSize: '0.9rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  transition: 'all 0.2s',
};

const secondaryBtn: React.CSSProperties = {
  flex: 1, padding: '12px', borderRadius: 12,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  color: '#64748b', fontWeight: 600, fontSize: '0.85rem',
  cursor: 'pointer', transition: 'all 0.2s',
};

const skipBtn: React.CSSProperties = {
  width: '100%', padding: '11px', borderRadius: 12,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  color: '#94a3b8', fontWeight: 600, fontSize: '0.8rem',
  cursor: 'pointer', marginTop: 10, transition: 'all 0.2s',
};

const footerNote: React.CSSProperties = {
  fontSize: '0.72rem', color: '#94a3b8', marginTop: 12, lineHeight: 1.5,
};

const tipBox: React.CSSProperties = {
  background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 12,
  padding: '12px 16px', fontSize: '0.78rem', color: '#92400e',
  lineHeight: 1.5, width: '100%', textAlign: 'left',
};

const successBox: React.CSSProperties = {
  width: '100%', padding: '12px', borderRadius: 14,
  background: '#f0fdf4', border: '1.5px solid #bbf7d0',
  marginBottom: 12,
};

const badge: React.CSSProperties = {
  fontSize: '0.72rem', fontWeight: 600, padding: '4px 10px', borderRadius: 20,
};
